import type { FC, ReactNode } from "react";
import { Fragment, memo } from "react";

import classNames from "classnames";

import { BagIcon } from "@jti/brand-icons/bag";
import { Icon } from "@jti/core-components";
import { Separator } from "@jti/core-components/components/Separator";
import type { Product as CartItem } from "@jti/magento";
import { useTranslations } from "@jti/ui";

import { CheckoutErrorSummary } from "@checkout/components";

import styles from "./CartItemList.module.scss";

type CartItemListProps = {
  items: CartItem[];
  emptyMessage?: string;
  renderItem(item: CartItem): ReactNode;
  onRemoveItems(itemIds: string[]): Promise<void>;
  className?: string;
  emptyClassName?: string;
  testId?: string;
  variant?: "regular" | "compact";
};

export const CartItemList: FC<CartItemListProps> = memo(
  ({
    items,
    emptyMessage,
    renderItem,
    onRemoveItems,
    className = "",
    emptyClassName = "",
    testId = "",
    variant = "regular",
  }) => {
    const { __ } = useTranslations();
    const hasItems = items?.length > 0;

    const resolvedEmptyMessage = emptyMessage ?? __("cart.item_list.no_items");

    if (variant === "compact") {
      if (hasItems) {
        const renderedItems = items.flatMap((item, index) => {
          const renderedItem = renderItem(item);

          return renderedItem === null || renderedItem === undefined || typeof renderedItem === "boolean"
            ? []
            : [{ key: item.id ?? `${item.sku}-${index}`, renderedItem }];
        });

        return (
          <div className={className} data-testid={testId}>
            <CheckoutErrorSummary
              items={items}
              onRemoveItems={onRemoveItems}
              variant={variant}
              className={styles.errorSummaryBlock}
            />
            {renderedItems.map(({ key, renderedItem }, index) => (
              <Fragment key={key}>
                {renderedItem}
                {index < renderedItems.length - 1 && <Separator />}
              </Fragment>
            ))}
          </div>
        );
      }

      return (
        <>
          <div data-testid="emptyCartContainer" className={emptyClassName}>
            <Icon icon={BagIcon} size="lg" className={styles.emptyStateIcon} />
            <div className={styles.emptyStateText}>{resolvedEmptyMessage}</div>
          </div>
        </>
      );
    } else if (hasItems) {
      return (
        <>
          <CheckoutErrorSummary
            items={items}
            onRemoveItems={onRemoveItems}
            className={styles.checkoutErrorSummary}
            variant={variant}
          />
          <div
            data-testid={testId}
            className={classNames({
              [className]: !!className,
              [styles.container]: true,
            })}
          >
            {items.map((item) => renderItem(item))}
          </div>
        </>
      );
    }

    return (
      <div
        data-testid="emptyCartContainer"
        className={classNames({
          [emptyClassName]: !!emptyClassName,
          [styles.emptyMessage]: true,
        })}
      >
        {resolvedEmptyMessage}
      </div>
    );
  }
);
